import { Fragment, useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Fieldset } from 'primereact/fieldset';
import { ToastService, useDialogCrup, useForm } from '@iamsoftware/react-hooks';
import { CommonService } from '../service/CommonService';

const partyTypes = [
    { value: 'PtyOrganization', label: 'Tổ chức' },
    { value: 'PtyPerson', label: 'Cá nhân' }
];

interface UseCustomerDialogOptions {
    service: {
        create: (data: any) => Promise<any>;
        update?: (id: string, data: any) => Promise<any>;
        get?: (id: string) => Promise<any>;
    };
    requireTaxCode?: boolean;
    onAfterCreate?: (resp: any) => void;
    onAfterUpdate?: (resp: any) => void;
    reloadLazyData?: () => void;
}

export const useCustomerDialog = (options: UseCustomerDialogOptions) => {
    const { service, requireTaxCode = false, onAfterCreate, onAfterUpdate, reloadLazyData } = options;

    const [readOnly, setReadOnly] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [provinces, setProvinces] = useState<any[]>([]);
    const [counties, setCounties] = useState<any[]>([]);
    const [partyTax, setPartyTax] = useState<string | null>(null);
    const [lastSearchedTax, setLastSearchedTax] = useState<string | null>(null);
    const [partyName, setPartyName] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [idValue, setIdValue] = useState('');
    const [partyTypeEnumId, setPartyTypeEnumId] = useState('PtyOrganization');

    useEffect(() => {
        CommonService.getProvinces().then(({ listData }: any) => {
            setProvinces(listData.map((item: any) => ({
                value: item?.toGeoId, label: item?.geoName
            })));
        });
    }, []);

    const formRepresentative = useForm({
        fields: [
            { field: 'representativeName', header: 'Tên người đại diện', className: 'md:col-6' },
            { field: 'representativeJobTitle', header: 'Chức vụ người đại diện', className: 'md:col-6' }
        ],
        readOnly
    });

    const validateFormData = (item: any): string | null => {
        // Validate partyName
        if (!partyName || partyName.trim().length === 0) {
            return 'Vui lòng nhập Tên khách hàng';
        }

        // Validate tax code
        if (requireTaxCode) {
            if (!partyTax || partyTax.trim().length === 0) {
                return 'Vui lòng nhập Mã số thuế';
            }
        }
        if (partyTax) {
            const trimmedTaxCode = String(partyTax).trim();
            if (trimmedTaxCode.length > 0 && ![10, 12, 13].includes(trimmedTaxCode.length)) {
                return 'Mã số thuế phải có 10, 12 hoặc 13 số';
            }
        }

        // Validate email
        if (item.emailAddress) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(item.emailAddress)) {
                return 'Địa chỉ email không hợp lệ';
            }
        }

        // Validate phone
        if (contactNumber) {
            const trimmed = contactNumber.trim();
            if (trimmed.length > 0 && trimmed.length !== 10) {
                return 'Số điện thoại phải có đúng 10 số';
            }
        }

        // Validate CCCD/CMND
        if (idValue) {
            const trimmed = idValue.trim();
            if (trimmed.length > 0 && ![9, 12].includes(trimmed.length)) {
                return 'CCCD/CMND phải có 9 hoặc 12 số';
            }
        }

        return null;
    };

    const inject = (item: any) => {
        const _valueFormRep = formRepresentative.getRawValue();
        item = { ...item, ..._valueFormRep };
        item.partyTaxId = partyTax;
        item.partyName = partyName || item.partyName;
        item.contactNumber = contactNumber || item.contactNumber;
        item.idValue = idValue || item.idValue;
        return item;
    };

    const { render, update, create, view, form } = useDialogCrup({
        header: 'khách hàng',
        dataKey: 'partyId',
        width: '80rem',
        fields: [
            { field: 'pseudoId', header: 'Mã khách hàng', className: 'md:col-3', disabled: isUpdate, InputTextProps: { placeholder: 'Tự động sinh mã' } },
            {
                field: 'partyTypeEnumId',
                header: 'Loại khách hàng',
                required: true,
                className: 'md:col-3',
                type: 'Dropdown',
                DropdownProps: {
                    options: partyTypes,
                    onChange: (e: any) => {
                        const newType = e.value;
                        setPartyTypeEnumId(newType);
                        const currentFormValue = form.getRawValue();
                        form.setValue({ ...currentFormValue, partyTypeEnumId: newType });
                        if (newType === 'PtyOrganization' && idValue) {
                            setIdValue('');
                        }
                    }
                }
            },
            {
                field: 'partyName', header: 'Tên khách hàng', required: true, className: 'md:col-6', type: 'custom',
                body: <Fragment>
                    <label>
                        Tên khách hàng <span style={{ color: 'red' }}>*</span>
                    </label>
                    <InputText
                        value={partyName}
                        onInput={(e) => {
                            const inputEl = e.target as HTMLInputElement;
                            const value = inputEl.value.replace(/\d/g, '');
                            inputEl.value = value;
                            setPartyName(value);
                        }}
                        onChange={(e) => setPartyName((e.target as HTMLInputElement).value)}
                        pattern="[a-zA-ZÀ-ỹ\s-]+"
                        title="Chỉ được nhập chữ cái và khoảng trắng"
                        placeholder="Nhập tên khách hàng"
                        disabled={readOnly}
                    />
                </Fragment>
            },
            {
                field: 'partyTaxId', header: 'Mã số thuế', required: requireTaxCode, className: 'md:col-3', type: 'custom',
                body: <Fragment>
                    <label>Mã số thuế {requireTaxCode && <span style={{ color: 'red' }}>*</span>}</label>
                    <div className="p-inputgroup flex-1">
                        <InputText
                            value={partyTax || ''}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 13);
                                e.target.value = value;
                                setPartyTax(value);
                            }}
                            onPaste={(e) => {
                                e.preventDefault();
                                const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                                const cleaned = paste.replace(/[^0-9]/g, '').slice(0, 13);
                                setPartyTax(cleaned);
                            }}
                            maxLength={13}
                            pattern="[0-9]*"
                            inputMode="numeric"
                            placeholder="Nhập mã số thuế"
                            disabled={readOnly}
                        />
                        <span className="p-inputgroup-addon">
                            <Button icon="pi pi-search" className="p-button-info" size="small" onClick={() => doGetPartyByTax(partyTax)} />
                        </span>
                    </div>
                </Fragment>
            },
            {
                field: 'contactNumber', header: 'Số điện thoại', className: 'md:col-3', type: 'custom',
                body: <Fragment>
                    <label>Số điện thoại</label>
                    <InputText
                        value={contactNumber}
                        onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                            setContactNumber(value);
                            if (form) {
                                form.setValue({ ...form.getRawValue(), contactNumber: value });
                            }
                        }}
                        onPaste={(e) => {
                            e.preventDefault();
                            const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                            const cleaned = paste.replace(/[^0-9]/g, '').slice(0, 10);
                            setContactNumber(cleaned);
                            if (form) {
                                form.setValue({ ...form.getRawValue(), contactNumber: cleaned });
                            }
                        }}
                        maxLength={10}
                        type="tel"
                        placeholder="Nhập số điện thoại"
                        disabled={readOnly}
                    />
                </Fragment>
            },
            { field: 'bankName', header: 'Tên ngân hàng', className: 'md:col-3' },
            { field: 'accountNumber', header: 'Số tài khoản', className: 'md:col-3' },
            {
                field: 'idValue', header: 'Số CCCD/CMND', className: 'md:col-3',
                disabledDependency(item: any): boolean {
                    return item.partyTypeEnumId === 'PtyOrganization';
                },
                type: 'custom',
                body: (item: any) => <Fragment>
                    <label>Số CCCD/CMND</label>
                    <InputText
                        value={idValue}
                        onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 12);
                            setIdValue(value);
                            if (form) {
                                form.setValue({ ...form.getRawValue(), idValue: value });
                            }
                        }}
                        onPaste={(e) => {
                            e.preventDefault();
                            const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                            const cleaned = paste.replace(/[^0-9]/g, '').slice(0, 12);
                            setIdValue(cleaned);
                            if (form) {
                                form.setValue({ ...form.getRawValue(), idValue: cleaned });
                            }
                        }}
                        maxLength={12}
                        placeholder="Nhập số CCCD/CMND"
                        disabled={readOnly || item?.partyTypeEnumId === 'PtyOrganization'}
                    />
                </Fragment>
            },
            {
                field: 'emailAddress', header: 'Địa chỉ email', className: 'md:col-3',
                InputTextProps: { type: 'email' }
            },
            { field: 'stateProvinceGeoId', header: 'Tỉnh/Thành phố', className: 'md:col-3', type: 'Dropdown', DropdownProps: { options: provinces, filter: true, filterBy: 'label', filterPlaceholder: 'Tìm kiếm tỉnh/thành phố' } },
            { field: 'countyGeoId', header: 'Phường/Xã', className: 'md:col-3', type: 'Dropdown', DropdownProps: { options: counties, filter: true, filterBy: 'label', filterPlaceholder: 'Tìm kiếm phường/xã' } },
            { field: 'address1', header: 'Địa chỉ', className: 'md:col-12' },
            {
                type: 'custom', className: 'md:col-12',
                displayDependency(item: any): boolean {
                    return item.partyTypeEnumId === 'PtyOrganization';
                },
                body: <Fieldset legend="Người đại diện">
                    {formRepresentative.render()}
                </Fieldset>
            }
        ],
        async createItem(item: any): Promise<any> {
            try {
                item.partyName = partyName;
                if (contactNumber) item.contactNumber = contactNumber;
                if (idValue) item.idValue = idValue;

                // Tự sinh mã KH trên backend nếu để trống
                const validationError = validateFormData(item);
                if (validationError) {
                    ToastService.error(validationError);
                    return Promise.reject(validationError);
                }

                const resp = await service.create(inject(item));

                if (resp && resp.success === 'Y') {
                    ToastService.success('Tạo khách hàng thành công');
                    if (reloadLazyData) reloadLazyData();
                    if (onAfterCreate) onAfterCreate(resp);
                    return resp;
                } else {
                    const msg = resp?.error || resp?.message || 'Tạo khách hàng thất bại';
                    ToastService.error(msg);
                    return Promise.reject(msg);
                }
            } catch (error) {
                ToastService.error(error);
                return Promise.reject(error);
            }

        },
        async updateItem(id: string, item: any): Promise<any> {
            try {
                if (!service.update) return Promise.reject('Update not supported');

                item.partyName = partyName || item.partyName;
                if (contactNumber) item.contactNumber = contactNumber;
                if (idValue) item.idValue = idValue;

                const validationError = validateFormData(item);
                if (validationError) {
                    ToastService.error(validationError);
                    return Promise.reject(validationError);
                }

                const resp = await service.update(id, inject(item));
                if (resp && resp.success === 'Y') {
                    ToastService.success('Cập nhật khách hàng thành công');
                    if (reloadLazyData) reloadLazyData();
                    if (onAfterUpdate) onAfterUpdate(resp);
                    return resp;
                } else {
                    const msg = resp?.error || resp?.message || 'Cập nhật khách hàng thất bại';
                    ToastService.error(msg);
                    return Promise.reject(msg);
                }

            } catch (error) {
                ToastService.error(error);
                return Promise.reject(error);
            }

        },
        reloadLazyData
    });

    const { stateProvinceGeoId } = form.getRawValue();

    // Load counties when province changes
    useEffect(() => {
        if (stateProvinceGeoId) {
            CommonService.getCounties(stateProvinceGeoId).then(({ listData }: any) => {
                setCounties(listData.map((item: any) => ({
                    value: item?.toGeoId, label: item?.geoName
                })));
            });
        }
    }, [stateProvinceGeoId]);

    const doGetPartyByTax = (partyTaxId: string | null) => {
        if (!partyTaxId || !partyTaxId.trim()) {
            ToastService.error('Vui lòng nhập mã số thuế');
            return;
        }

        const clearData = () => {
            setPartyName('');
            setContactNumber('');
            setIdValue('');
            setCounties([]);
            formRepresentative.reset();
            const _fromValue = form.getRawValue();
            const clearedValue = { ..._fromValue };
            clearedValue.pseudoId = '';
            clearedValue.partyName = '';
            clearedValue.contactNumber = '';
            clearedValue.idValue = '';
            clearedValue.emailAddress = '';
            clearedValue.stateProvinceGeoId = null;
            clearedValue.countyGeoId = null;
            clearedValue.address1 = '';
            clearedValue.bankName = '';
            clearedValue.accountNumber = '';
            clearedValue.representativeName = '';
            clearedValue.representativeJobTitle = '';
            form.setValue(clearedValue);
        };

        CommonService.getPartyByTax(partyTaxId).then((response: any) => {
            const data = response?.data || response;

            if (response?.success === 'N' || response?.error || data?.errors || (data && data.success === 'N') || (data && data.error)) {
                const errorMsg = response?.errors || data?.errors || response?.message || data?.error || data?.message || 'Không tìm thấy thông tin khách hàng với mã số thuế này';
                ToastService.error(errorMsg);
                clearData();
                return;
            }

            if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
                ToastService.error('Không tìm thấy thông tin khách hàng với mã số thuế này');
                clearData();
                return;
            }

            const isEmpty = (value: any) => {
                return value === null || value === undefined || value === '' || String(value).trim() === '';
            };

            const _fromValue = form.getRawValue();
            const _valueFormRep = formRepresentative.getRawValue();
            const newValue = { ..._fromValue, ...data, ..._valueFormRep };

            setLastSearchedTax(partyTaxId);
            formRepresentative.setValue(newValue);
            form.setValue(newValue);
            if (data.partyName) setPartyName(data.partyName);
            if (data.contactNumber || data.phoneNumber) setContactNumber(data.contactNumber || data.phoneNumber || '');
            if (data.idValue) setIdValue(data.idValue);

            // Tra cứu DKKD bổ sung
            CommonService.getPartyFromDKKDAutomated(partyTaxId).then((dkkdData: any) => {
                if (dkkdData && dkkdData.length > 0) {
                    const ent = dkkdData[0];
                    const finalUpdate = { ...newValue };

                    if (ent.Name) {
                        finalUpdate.partyName = ent.Name;
                        setPartyName(ent.Name);
                    }
                    if (ent.Legal_First_Name) {
                        finalUpdate.representativeName = ent.Legal_First_Name;
                    }
                    if (ent.Legal_Job_Title) {
                        finalUpdate.representativeJobTitle = ent.Legal_Job_Title;
                    }

                    form.setValue(finalUpdate);
                    formRepresentative.setValue(finalUpdate);
                }
            }).catch((err: any) => {
                console.warn('Lỗi tra cứu DKKD:', err);
            });
        }).catch((error: any) => {
            const errorMessage = error?.response?.data?.errors || error?.response?.data?.message ||
                error?.response?.data?.error || error?.response?.data?.errorMessage ||
                error?.message || `Không thể tìm kiếm thông tin khách hàng với MST : ${partyTaxId}`;
            ToastService.error(errorMessage);
            clearData();
        });
    };

    const resetStates = () => {
        setPartyTax(null);
        setPartyName('');
        setContactNumber('');
        setIdValue('');
        setReadOnly(false);
        setIsUpdate(false);
        formRepresentative.reset();
    };

    const doCreate = (initialData?: any) => {
        resetStates();
        const defaultData = { partyTypeEnumId: 'PtyOrganization', ...initialData };
        setPartyTypeEnumId(defaultData.partyTypeEnumId);
        create(defaultData);
    };

    const doUpdate = (item: any) => {
        if (!service.get || !item.partyId) return;
        service.get(item.partyId).then((data: any) => {
            formRepresentative.setValue(data);
            setPartyTax(data.partyTaxId || null);
            setPartyName(data.partyName || '');
            setContactNumber(data.contactNumber || '');
            setIdValue(data.idValue || '');
            setReadOnly(false);
            setIsUpdate(true);
            update(data);
        });
    };

    const doView = (item: any) => {
        if (!service.get || !item.partyId) return;
        service.get(item.partyId).then((data: any) => {
            formRepresentative.setValue(data);
            setPartyTax(data.partyTaxId || null);
            setPartyName(data.partyName || '');
            setContactNumber(data.contactNumber || '');
            setIdValue(data.idValue || '');
            setReadOnly(true);
            setIsUpdate(false);
            view(data);
        });
    };

    return {
        render,
        form,
        create: doCreate,
        update: doUpdate,
        view: doView,
        resetStates,
        setPartyTax,
        setPartyName,
        setContactNumber,
        setIdValue,
        setPartyTypeEnumId,
        setReadOnly,
        partyTax,
        partyName,
        contactNumber,
        idValue,
        partyTypeEnumId,
    };
};
